import { useCallback, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import {
  buildCatalogueIframeUrl,
  searchVehicleByLpn,
} from 'lib/api/vehicle-catalog';

function getApiErrorMessage(error, fallback) {
  return (
    error?.response?.data?.errorMsg ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
}

export function useCatalogueVehicleSearch(onCatalogueReady) {
  const [vehicleSearchValues, setVehicleSearchValues] = useState({});
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(true);
  const [validationError, setValidationError] = useState('');

  const handleVehicleSearchValueChange = useCallback((field, value) => {
    setVehicleSearchValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const { isValid: isVehicleSearchValuesValid, errorMessage } = useMemo(() => {
    const hasCarMaker = Boolean(vehicleSearchValues.carMaker);
    const hasModelLine = Boolean(vehicleSearchValues.modelLine);
    const hasYear = Boolean(vehicleSearchValues.year);
    const hasModification = Boolean(vehicleSearchValues.modification);
    const hasOemUrl = Boolean(vehicleSearchValues.oemUrl);

    if (hasModification && !hasOemUrl) {
      return {
        isValid: false,
        errorMessage: 'Catalogue is not available for this vehicle',
      };
    }

    return {
      isValid:
        hasCarMaker && hasModelLine && hasYear && hasModification && hasOemUrl,
      errorMessage: '',
    };
  }, [vehicleSearchValues]);

  const openVehicleModal = useCallback(() => {
    setVehicleSearchValues({});
    setValidationError('');
    setIsVehicleModalOpen(true);
  }, []);

  const closeVehicleModal = useCallback(() => {
    setIsVehicleModalOpen(false);
  }, []);

  const handleVehicleSearchError = useCallback((message) => {
    setValidationError(message);
  }, []);

  const handleVehicleSearchConfirm = useCallback(
    (values) => {
      const oemUrl = values.modification?.oemUrl || vehicleSearchValues.oemUrl;

      if (!oemUrl) {
        return;
      }

      onCatalogueReady(buildCatalogueIframeUrl(oemUrl));
      setIsVehicleModalOpen(false);
      setVehicleSearchValues({});
      setValidationError('');
    },
    [onCatalogueReady, vehicleSearchValues.oemUrl],
  );

  const lpnSearchMutation = useMutation(searchVehicleByLpn, {
    onSuccess: (vehicleData) => {
      const {
        makerId,
        lineId,
        year,
        configurationId,
        oemUrl,
        numberPlate,
      } = vehicleData || {};

      if (oemUrl) {
        onCatalogueReady(buildCatalogueIframeUrl(oemUrl));
        setIsVehicleModalOpen(false);
        setVehicleSearchValues({});
        setValidationError('');
        return;
      }

      if (vehicleData && makerId) {
        setVehicleSearchValues((prev) => ({
          ...prev,
          licensePlateNumber: numberPlate,
          carMaker: makerId?.toString(),
          modelLine: lineId?.toString(),
          year: year?.toString(),
          modification: configurationId?.toString(),
          oemUrl,
          isCarMakerPreFilled: Boolean(makerId),
          isModelLinePreFilled: Boolean(lineId),
          isYearPreFilled: Boolean(year),
          isModificationPreFilled: Boolean(configurationId),
          isLicensePlateNumberPreFilled: Boolean(numberPlate),
        }));
        setValidationError('');
        return;
      }

      setVehicleSearchValues({});
      setValidationError('No vehicle information found for this LPN');
    },
    onError: (error) => {
      setValidationError(
        getApiErrorMessage(error, 'Error searching for vehicle information'),
      );
    },
  });

  const handleLpnSearch = useCallback(
    (licensePlateNumber) => {
      lpnSearchMutation.mutate(licensePlateNumber);
    },
    [lpnSearchMutation],
  );

  return {
    vehicleSearchValues,
    isVehicleModalOpen,
    isVehicleSearchValuesValid,
    errorMessage: validationError || errorMessage,
    isLpnSearching: lpnSearchMutation.isLoading,
    openVehicleModal,
    closeVehicleModal,
    handleVehicleSearchValueChange,
    handleVehicleSearchConfirm,
    handleVehicleSearchError,
    handleLpnSearch,
  };
}
